import { useBloc } from "@blac/react";
import React, { memo, useRef, useEffect } from "react";
import { FC } from "react";
import styled from "@emotion/styled";
import Loader from "../Loader/Loader";
import { keyframes } from "@emotion/react";
import clsx from "clsx";
import { KnownProgram } from "src/constants/programs";
import JourneyModuleTrigger from "./JourneyModuleTrigger";
import UserJourneyBloc from "./UserJourneyBloc";
import Translate from "src/ui/components/Translate/Translate";
import ClockGIF from "src/ui/assets/images/clock.gif";
import translate from "src/lib/translate";
import { JourneyModuleHeader } from "./JourneyModuleHeader";
import { JourneyCard } from "components/UserJourneyOverview/JourneyCard";
import Spacer from "components/UserJourneyOverview/Spacer";

const JourneyCardWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 0 -4rem 0;

  @media (max-width: 768px) {
    margin: 0;
  }
`;

const JourneyCardRow = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ComingSoon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;
  padding-bottom: 9rem;
  position: relative;
`;

const Gradient = styled.div`
  position: absolute;
  background: var(--gradient-mid-morning-360);
  width: 100vw;
  height: 50rem;
  z-index: -1;
`;

const H3 = styled.h3`
  @media (min-width: 605px) {
    width: 30rem;
  }
`;

const Clock = styled.div`
  background-color: white;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  padding: 0.375rem;
`;

const ModuleList = styled.div`
  animation: ${fadeIn} 0.5s ease-in-out;
  h2 {
    text-align: center;
    margin: 0 0 1.2rem 0;
    padding: 0 1.5rem;
    text-wrap: pretty;
    font-size: 2.25rem;

    em {
      display: block;
      text-align: center;
      font-size: 0.875rem;
      font-style: normal;
      font-weight: 600;
      letter-spacing: -0.0175rem;
      text-transform: capitalize;
      font-family: var(--font-family);
      line-height: 2.4;
      color: var(--color-charcoal-80);
    }
  }
`;

const UserJourneyOverview: FC<{ program: KnownProgram | null }> = memo(
  (props) => {
    const [
      { loading, modules = [], journeyCardsLocked },
      {
        onTaskStatusUpdated,
        isNutritionJourneyCardVisible,
        isMindsetJourneyCardVisible,
        isActivityJourneyCardVisible,
        availableJourneysToEnroll
      }
    ] = useBloc(UserJourneyBloc, {
      props: {
        journey:
          props.program === KnownProgram.PATH_HEALTHY_WEIGHT_HWJ_12W
            ? "life-balance-12-week"
            : "everyday-diabetes-journey",
        taskProgram: props.program ?? KnownProgram.PATH_HEALTHY_WEIGHT_HWJ_12W
      }
    });

    const journeyCardRowRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const scrollToCards = () => {
        if (
          journeyCardsLocked === false &&
          availableJourneysToEnroll &&
          journeyCardRowRef.current
        ) {
          journeyCardRowRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        } else if (journeyCardsLocked === false && availableJourneysToEnroll) {
          // Retry after a short delay if ref isn't ready
          setTimeout(scrollToCards, 100);
        }
      };

      scrollToCards();
    }, [journeyCardsLocked, availableJourneysToEnroll]);

    if (loading) {
      return <Loader fixed active />;
    }

    return (
      <ModuleList>
        {modules.map((module, moduleIndex) => (
          <div
            key={module.name}
            className={clsx({
              "module-list-item": true,
              last: moduleIndex === modules.length - 1
            })}
          >
            <JourneyModuleHeader module={module} first={moduleIndex === 0} />
            {module.lessons.map((lesson, lessonIndex) => (
              <div
                key={lesson.name}
                className={clsx({
                  "lesson-list-item": true,
                  last: lessonIndex === module.lessons.length - 1
                })}
              >
                {lesson.items.map((item) => (
                  <JourneyModuleTrigger
                    key={item.type}
                    item={item}
                    lesson={lesson}
                    module={module}
                    onTaskStatusUpdated={(status) =>
                      onTaskStatusUpdated(item.task, status)
                    }
                  />
                ))}
              </div>
            ))}
          </div>
        ))}

        {availableJourneysToEnroll ? (
          <JourneyCardWrapper>
            <Gradient />

            <Spacer colorScheme="midMorning" />

            <h2>
              {journeyCardsLocked
                ? translate("journey.chooseYourNextAdvancedJourney.locked")
                : translate("journey.chooseYourNextAdvancedJourney")}
            </h2>

            <JourneyCardRow ref={journeyCardRowRef}>
              {isNutritionJourneyCardVisible && (
                <JourneyCard
                  locked={journeyCardsLocked}
                  journey="nutrition-journey"
                  colorScheme="goldenHour"
                />
              )}
              {isMindsetJourneyCardVisible && (
                <JourneyCard
                  locked={journeyCardsLocked}
                  journey="mindset-journey"
                  colorScheme="hazySundown"
                />
              )}
              {isActivityJourneyCardVisible && (
                <JourneyCard
                  locked={journeyCardsLocked}
                  journey="physical-activity-journey"
                  colorScheme="fierceSundown"
                />
              )}
            </JourneyCardRow>
          </JourneyCardWrapper>
        ) : (
          <ComingSoon>
            <Spacer colorScheme="midMorning" />
            <Gradient />
            <Clock aria-hidden>
              <img src={ClockGIF} alt={translate("clock.alt")} />
            </Clock>
            <H3>
              <Translate msg="lifestyle.comingSoon" />
            </H3>
            <p className="as-subhead">
              <Translate msg="lifestyle.comingSoon.description" />
            </p>
          </ComingSoon>
        )}
      </ModuleList>
    );
  }
);

export default UserJourneyOverview;
